/* eslint-disable @typescript-eslint/no-explicit-any */
import IndeterminateCheckbox from 'components/base/IndeterminateCheckbox';
import { PropsWithChildren, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  ColumnDef,
  InitialTableState
} from '@tanstack/react-table';
import { Placeholder } from 'react-bootstrap';
interface UseAdvanceTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  selection?: boolean;
  pagination?: boolean;
  pageSize?: number;
  selectionColumnWidth?: number | string;
  initialState?: InitialTableState;
  totalRows?: number;
  page?: number;
  isLoading?: boolean;
}

const selectionColumn = {
  id: 'select',
  accessorKey: '',
  header: ({ table }: any) => (
    <IndeterminateCheckbox
      className="form-check fs-8 mb-0"
      {...{
        checked: table.getIsAllRowsSelected(),
        indeterminate: table.getIsSomeRowsSelected(),
        onChange: table.getToggleAllRowsSelectedHandler()
      }}
    />
  ),
  cell: ({ row }: any) => (
    <IndeterminateCheckbox
      className="form-check fs-8 mb-0"
      {...{
        checked: row.getIsSelected(),
        disabled: !row.getCanSelect(),
        indeterminate: row.getIsSomeSelected(),
        onChange: row.getToggleSelectedHandler()
      }}
    />
  ),
  meta: {
    headerProps: { style: { width: '30px' } }
  }
};

const useAdvanceServerTable = <T,>({
  columns,
  data,
  selection,
  pagination,
  pageSize = 10,
  initialState,
  totalRows,
  page,
  isLoading
}: PropsWithChildren<UseAdvanceTableProps<T>>) => {
  const state = {
    pagination: pagination
      ? {
          pageSize: pagination ? pageSize : data.length,
          pageIndex: page ? page - 1 : undefined
        }
      : undefined,
    ...initialState
  };
  const tableColumns = selection ? [selectionColumn, ...columns] : columns;

  const tableData = useMemo(() => {
    return isLoading ? [...Array(5)] : data;
  }, [isLoading]);

  const lazyColumns = useMemo(() => {
    return isLoading
      ? tableColumns.map((item, idx) => ({
          ...item,
          cell: () => (selection && idx === 0 ? <></> : <Placeholder xs={6} />)
        }))
      : tableColumns;
  }, [isLoading]);

  const table = useReactTable<T>({
    data: tableData,
    columns: lazyColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: state,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: totalRows ? Math.ceil(totalRows / pageSize) : undefined
  });

  return table;
};

export default useAdvanceServerTable;
